import React from 'react';
import { Users, Target, Award, MessageCircle, ExternalLink, Calendar } from 'lucide-react';
import b2cData from '../../data/public/b2c-portfolio.json';

// B2C 페이지 컴포넌트 - 개인 대상 챌린지 프로그램 정보
const B2C: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 히어로 섹션 */}
      <section className="bg-gradient-to-r from-secondary-600 to-secondary-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <Users className="w-12 h-12 text-white mr-4" />
            <h1 className="text-4xl font-bold text-white">B2C 챌린지 프로그램</h1>
          </div>
          <p className="text-xl text-secondary-100 max-w-3xl mx-auto mb-8">
            공공기관 교육 수료생들을 대상으로 한<br />
            지속적인 학습과 실전 적용을 위한 챌린지 프로그램
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-secondary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
              <MessageCircle className="w-5 h-5" />
              <span>카카오톡 채널 가입</span>
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-secondary-600 transition-colors flex items-center justify-center space-x-2">
              <Target className="w-5 h-5" />
              <span>챌린지 참여하기</span>
            </button>
          </div>
        </div>
      </section>

      {/* 프로그램 소개 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              지속적인 학습을 위한 챌린지 프로그램
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              공공기관에서 ChatGPT 교육을 수료한 분들을 대상으로<br />
              실전 미션과 챌린지를 통해 지속적인 성장을 지원합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">실전 미션</h3>
              <p className="text-gray-600">
                개인별 맞춤 미션을 통해<br />
                ChatGPT 활용 능력을 단계적으로 향상
              </p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">커뮤니티</h3>
              <p className="text-gray-600">
                카카오톡 채널을 통한<br />
                참여자 간 소통과 피드백 공유
              </p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-secondary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">성과 인증</h3>
              <p className="text-gray-600">
                챌린지 완주 시 수료증 발급 및<br />
                개인 포트폴리오 구축 지원
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 챌린지 사례 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              챌린지 프로그램 사례
            </h2>
            <p className="text-xl text-gray-600">
              다양한 주제의 챌린지 프로그램과 참여자 성과
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {b2cData.slice(0, 6).map((item: any) => (
              <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.강의주제}
                    </h3>
                    <div className="text-sm text-secondary-600 font-medium">
                      {item.날짜}
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">참여자</span>
                      <span className="font-medium">{item.결과.참여자수}명</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">완주율</span>
                      <span className="font-medium text-green-600">{item.결과.완주율}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">성공률</span>
                      <span className="font-medium text-green-600">
                        {item.결과.취업성공률 || item.결과.창업성공률 || item.결과.실무적용률}%
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">만족도</span>
                      <span className="font-medium">{item.결과.만족도}/5.0</span>
                    </div>
                  </div>

                  {item.블로그링크 && (
                    <a
                      href={item.블로그링크}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-secondary-600 hover:text-secondary-700 font-medium text-sm"
                    >
                      <span>자세히 보기</span>
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 프로그램 특징 */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              챌린지 프로그램 특징
            </h2>
            <p className="text-xl text-gray-600">
              체계적이고 지속적인 학습을 위한 특별한 프로그램
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-secondary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">개인 맞춤 미션</h3>
                  <p className="text-gray-600">
                    참여자의 현재 수준과 목표에 맞는 개인별 미션을 제공하여 
                    단계적으로 실력을 향상시킵니다.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-secondary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">4주 완성 프로그램</h3>
                  <p className="text-gray-600">
                    체계적인 4주 커리큘럼으로 집중적이고 효과적인 
                    학습 경험을 제공합니다.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-4 h-4 text-secondary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">실시간 소통</h3>
                  <p className="text-gray-600">
                    카카오톡 채널을 통한 실시간 질문과 답변, 
                    참여자 간의 경험 공유가 가능합니다.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Award className="w-4 h-4 text-secondary-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">성과 인증</h3>
                  <p className="text-gray-600">
                    챌린지 완주 시 수료증 발급과 함께 
                    개인 포트폴리오 구축을 지원합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 참여 방법 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              챌린지 참여 방법
            </h2>
            <p className="text-xl text-gray-600">
              간단한 3단계로 챌린지에 참여하세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">카카오톡 채널 가입</h3>
              <p className="text-gray-600">QR코드 스캔 또는 링크를 통해 채널에 가입</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">챌린지 신청</h3>
              <p className="text-gray-600">원하는 챌린지 프로그램을 선택하여 신청</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">미션 수행</h3>
              <p className="text-gray-600">제시된 미션을 수행하며 실력을 향상</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="py-16 bg-gradient-to-r from-secondary-600 to-secondary-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            지금 바로 챌린지에 참여하세요
          </h2>
          <p className="text-xl text-secondary-100 mb-8 max-w-3xl mx-auto">
            ChatGPT 활용 능력을 한 단계 더 발전시키고<br />
            실전 경험을 쌓아보세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-secondary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2">
              <MessageCircle className="w-5 h-5" />
              <span>카카오톡 채널 가입</span>
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-secondary-600 transition-colors flex items-center justify-center space-x-2">
              <Target className="w-5 h-5" />
              <span>챌린지 신청하기</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default B2C;

